import { useCallback, useEffect, useRef, useState, type ClipboardEvent as ReactClipboardEvent, type MutableRefObject } from "react";
import { api } from "../lib/api.js";
import type { Scene } from "../lib/types.js";
import { countCharacters, countWords } from "../lib/words.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useAutosave, type SaveState } from "./useAutosave.js";
import { useTts } from "./useTts.js";
import { useReadAlong } from "./useReadAlong.js";
import { TtsConsole } from "./TtsConsole.js";

/**
 * Imperative handle the AI panel uses to apply *accepted* suggestions. Every
 * method that changes text routes through the normal save path, so accepted AI
 * changes always enter version history. Nothing here is ever called without an
 * explicit user action (Accept / Insert).
 */
export interface EditorControls {
  /** The author's most recent non-empty selection within the editor. */
  getSelectedText: () => string;
  /** Replace that selection with the given text, then save (creates a revision). */
  replaceSelection: (text: string) => boolean;
  /** Append the text as new paragraphs at the end, then save (creates a revision). */
  insertText: (text: string) => void;
  saveNow: () => Promise<void>;
}

interface EditorProps {
  scene: Scene;
  autosaveIntervalMs: number;
  /** Sum of word counts of the OTHER scenes in this chapter (for live totals). */
  chapterBaseWords: number;
  /** Sum of word counts of the OTHER scenes in the project (for live totals). */
  projectBaseWords: number;
  /** Context + position for the header eyebrow and status bar. */
  chapterTitle: string;
  sceneIndex: number;
  sceneTotal: number;
  onSaved: (sceneId: string, wordCount: number, charCount: number) => void;
  onTitleChange: (sceneId: string, title: string) => void;
  onOpenHistory: () => void;
  onToggleInspector: () => void;
  onToggleFocus: () => void;
  onToggleAi: () => void;
  onEnterReview: () => void;
  aiCollapsed: boolean;
  /** Populated by the editor so the AI panel can apply accepted suggestions. */
  controlsRef?: MutableRefObject<EditorControls | null>;
  /** Reports the current non-empty selection text (for rewrite/critique). */
  onSelectionChange?: (text: string) => void;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Inline HTML: newlines → <br>, safe to drop inside an existing paragraph. */
function toInlineHtml(text: string): string {
  return escapeHtml(text.trim()).replace(/\n/g, "<br>");
}

/** Block HTML: blank-line-separated paragraphs, for appended continuations. */
function toParagraphsHtml(text: string): string {
  return text
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/**
 * Normalize pasted plain-text prose into clean novel paragraphs:
 * every line break starts a new paragraph (so a new speaker's dialogue on its
 * own line becomes its own paragraph), and blank lines are dropped so no empty
 * paragraph nodes are created. Book-style spacing comes from CSS, not markup.
 */
function normalizePastedText(text: string): string {
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
}

const TYPEWRITER_KEY = "clovascribe:typewriter";

const SAVE_LABEL: Record<SaveState, string> = {
  saved: "Saved",
  saving: "Saving…",
  unsaved: "Unsaved changes",
  error: "Save failed — retrying"
};

/**
 * The distraction-free manuscript editor. Content is edited as rich HTML in a
 * contentEditable surface and persisted exactly as entered — no AI or
 * automatic transformation ever touches it. Formatting uses the browser's
 * built-in editing commands to stay dependency-free.
 */
export function Editor({
  scene,
  autosaveIntervalMs,
  chapterBaseWords,
  projectBaseWords,
  chapterTitle,
  sceneIndex,
  sceneTotal,
  onSaved,
  onTitleChange,
  onOpenHistory,
  onToggleInspector,
  onToggleFocus,
  onToggleAi,
  onEnterReview,
  aiCollapsed,
  controlsRef,
  onSelectionChange
}: EditorProps) {
  const toast = useToast();
  const editorRef = useRef<HTMLDivElement>(null);
  const lastSelectionRef = useRef<{ range: Range; text: string } | null>(null);
  const [live, setLive] = useState({ words: 0, chars: 0 });
  const [selectedWords, setSelectedWords] = useState(0);
  const [titleDraft, setTitleDraft] = useState(scene.title);
  const [editingTitle, setEditingTitle] = useState(false);
  // Typewriter focus: dim every paragraph except the one holding the caret.
  const [typewriter, setTypewriter] = useState(() => {
    try { return localStorage.getItem(TYPEWRITER_KEY) === "1"; } catch { return false; }
  });
  const typewriterRef = useRef(typewriter);

  const save = useCallback(
    async (content: string, reason: "autosave" | "manual") => {
      const res = await api.saveScene(scene.id, content, reason);
      onSaved(scene.id, res.scene.wordCount, res.scene.charCount);
    },
    [scene.id, onSaved]
  );

  const { state, schedule, saveNow, hasPending } = useAutosave({
    save,
    intervalMs: autosaveIntervalMs
  });

  const tts = useTts(scene.projectId);
  useReadAlong(editorRef, tts);

  const recomputeLive = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const text = el.innerText;
    setLive({ words: countWords(text), chars: countCharacters(text) });
  }, []);

  // Typewriter focus: mark the top-level block that holds the caret as active so
  // CSS can dim the rest. No-op (and cheap) when the mode is off.
  const markActiveParagraph = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const clear = () => el.querySelectorAll(".tw-active").forEach((n) => n.classList.remove("tw-active"));
    if (!typewriterRef.current) { clear(); return; }
    const sel = window.getSelection();
    let node: Node | null = sel && sel.rangeCount > 0 ? sel.anchorNode : null;
    if (!node || !el.contains(node)) return; // caret left the editor — keep last
    while (node && node.parentNode !== el) node = node.parentNode;
    if (!(node instanceof HTMLElement) || node.classList.contains("tw-active")) return;
    clear();
    node.classList.add("tw-active");
  }, []);

  // Keep the ref in sync and (de)activate dimming when the toggle changes.
  useEffect(() => {
    typewriterRef.current = typewriter;
    try { localStorage.setItem(TYPEWRITER_KEY, typewriter ? "1" : "0"); } catch { /* ignore */ }
    const el = editorRef.current;
    if (!el) return;
    if (typewriter) markActiveParagraph();
    else el.querySelectorAll(".tw-active").forEach((n) => n.classList.remove("tw-active"));
  }, [typewriter, markActiveParagraph]);

  // Initialise the surface once when the scene mounts. The parent keys this
  // component by scene id, so this runs exactly once per scene and never
  // clobbers the caret mid-edit.
  useEffect(() => {
    const el = editorRef.current;
    if (el) {
      el.innerHTML = scene.content || "";
      recomputeLive();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInput = () => {
    const el = editorRef.current;
    if (!el) return;
    schedule(el.innerHTML);
    recomputeLive();
    markActiveParagraph();
  };

  // Normalize pasted prose so it takes on clean novel paragraphs rather than
  // the browser's default markup (which stacks divs and injects empty
  // paragraph nodes, producing the large blank gaps).
  const onPaste = (e: ReactClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData("text/plain");
    if (!text) return; // let the browser handle non-text (e.g. images)
    e.preventDefault();
    const html = normalizePastedText(text);
    if (html) {
      document.execCommand("insertHTML", false, html);
      onInput();
    }
  };

  // Track the selection: live word count, plus the most recent NON-EMPTY
  // selection (range + text) so the AI panel can rewrite it even after focus
  // moves to the panel and collapses the visible selection.
  useEffect(() => {
    const onSelect = () => {
      markActiveParagraph(); // keep typewriter focus on the caret's paragraph
      const selection = window.getSelection();
      if (!selection || !editorRef.current) {
        setSelectedWords(0);
        return;
      }
      if (selection.isCollapsed) {
        setSelectedWords(0);
        return;
      }
      const anchor = selection.anchorNode;
      if (anchor && editorRef.current.contains(anchor)) {
        const text = selection.toString();
        setSelectedWords(countWords(text));
        if (text.trim().length > 0) {
          lastSelectionRef.current = { range: selection.getRangeAt(0).cloneRange(), text };
          onSelectionChange?.(text);
        }
      } else {
        setSelectedWords(0);
      }
    };
    document.addEventListener("selectionchange", onSelect);
    return () => document.removeEventListener("selectionchange", onSelect);
  }, [onSelectionChange, markActiveParagraph]);

  // Ctrl/Cmd+S manual save.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void saveNow("manual").then(() => toast.success("Saved"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saveNow, toast]);

  // Expose imperative controls so the AI panel can apply accepted suggestions.
  // Every mutation goes through the normal save (reason "manual"), so an
  // accepted rewrite or inserted continuation always becomes a revision.
  useEffect(() => {
    if (!controlsRef) return;
    const applyAndSave = () => {
      const el = editorRef.current;
      if (!el) return;
      schedule(el.innerHTML);
      recomputeLive();
      void saveNow("manual");
    };
    controlsRef.current = {
      getSelectedText: () => lastSelectionRef.current?.text ?? "",
      replaceSelection: (text: string) => {
        const el = editorRef.current;
        const stored = lastSelectionRef.current;
        if (!el || !stored) return false;
        el.focus();
        const selection = window.getSelection();
        if (!selection) return false;
        selection.removeAllRanges();
        selection.addRange(stored.range);
        document.execCommand("insertHTML", false, toInlineHtml(text));
        lastSelectionRef.current = null;
        applyAndSave();
        return true;
      },
      insertText: (text: string) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false); // caret at very end
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand("insertHTML", false, toParagraphsHtml(text));
        applyAndSave();
      },
      saveNow: () => saveNow("manual")
    };
    return () => {
      if (controlsRef.current) controlsRef.current = null;
    };
  }, [controlsRef, saveNow, schedule, recomputeLive]);

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    onInput();
  };

  const insertSceneBreak = () => {
    editorRef.current?.focus();
    // A centered dinkus is the classic scene divider. Insert as its own block.
    document.execCommand("insertHTML", false, '<p class="scene-break">⁂</p><p><br/></p>');
    onInput();
  };

  const commitTitle = () => {
    setEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== scene.title) {
      api
        .updateScene(scene.id, { title: trimmed })
        .then(() => onTitleChange(scene.id, trimmed))
        .catch((err) => toast.error(err instanceof Error ? err.message : "Rename failed"));
    } else {
      setTitleDraft(scene.title);
    }
  };

  const chapterWords = chapterBaseWords + live.words;
  const projectWords = projectBaseWords + live.words;

  return (
    <div className="editor">
      <header className="editor-head">
        <div className="grow" style={{ minWidth: 0 }}>
          {chapterTitle && <div className="editor-eyebrow truncate">{chapterTitle}{sceneTotal > 0 ? ` · Scene ${sceneIndex} of ${sceneTotal}` : ""}</div>}
          {editingTitle ? (
            <input
              className="editor-title-input"
              value={titleDraft}
              autoFocus
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitTitle();
                if (e.key === "Escape") {
                  setTitleDraft(scene.title);
                  setEditingTitle(false);
                }
              }}
            />
          ) : (
            <h2 className="editor-title truncate" onDoubleClick={() => setEditingTitle(true)} title="Double-click to rename">
              {scene.title}
            </h2>
          )}
        </div>
        <div className="row gap-2 editor-head-tools">
          <SaveIndicator state={state} hasPending={hasPending} />
          <TtsControls tts={tts} getText={() => editorRef.current?.innerText ?? ""} />
          <span className="editor-head-sep" />
          <button className="btn btn-icon btn-ghost" title="Version history" onClick={onOpenHistory}>
            <Icon name="history" size={16} />
          </button>
          <button className="btn btn-icon btn-ghost" title="Scene inspector" onClick={onToggleInspector}>
            <Icon name="info" size={16} />
          </button>
          <button className="btn btn-icon btn-ghost" title="Focus mode — distraction-free writing (Esc to exit)" onClick={onToggleFocus}>
            <Icon name="target" size={16} />
          </button>
          <button className="btn btn-icon btn-ghost" title="Review / Markup Mode" onClick={onEnterReview}>
            <Icon name="note" size={16} />
          </button>
          <button className={`btn btn-icon ${aiCollapsed ? "btn-ghost" : "btn-ai-on"}`} title={aiCollapsed ? "Open Scribe AI" : "Hide Scribe AI"} onClick={onToggleAi}>
            <Icon name="sparkles" size={16} />
          </button>
        </div>
      </header>

      <div className="editor-toolbar">
        <ToolButton icon="bold" title="Bold (Ctrl+B)" onClick={() => exec("bold")} />
        <ToolButton icon="italic" title="Italic (Ctrl+I)" onClick={() => exec("italic")} />
        <ToolButton icon="underline" title="Underline (Ctrl+U)" onClick={() => exec("underline")} />
        <span className="tool-sep" />
        <ToolButton icon="heading" title="Heading" onClick={() => exec("formatBlock", "H2")} />
        <ToolButton icon="quote" title="Block quote" onClick={() => exec("formatBlock", "BLOCKQUOTE")} />
        <ToolButton icon="file" title="Normal text" onClick={() => exec("formatBlock", "P")} />
        <ToolButton icon="minus" title="Scene break" onClick={insertSceneBreak} />
        <span className="tool-sep" />
        <ToolButton icon="undo" title="Undo (Ctrl+Z)" onClick={() => exec("undo")} />
        <ToolButton icon="redo" title="Redo (Ctrl+Shift+Z)" onClick={() => exec("redo")} />
        <span className="spacer" />
        <button
          className={`tool-btn ${typewriter ? "tool-btn-active" : ""}`}
          title="Typewriter focus — dim all but the current paragraph"
          aria-pressed={typewriter}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setTypewriter((v) => !v)}
        >
          <Icon name="target" size={15} />
        </button>
      </div>

      <div className="editor-scroll">
        <div
          ref={editorRef}
          className={`editor-surface${typewriter ? " tw-dim" : ""}`}
          contentEditable
          suppressContentEditableWarning
          spellCheck
          onInput={onInput}
          onPaste={onPaste}
          onBlur={() => void saveNow("autosave")}
          data-placeholder="Begin writing…"
        />
      </div>

      <footer className="editor-foot">
        <span className="foot-stat"><b>{live.words.toLocaleString()}</b> words</span>
        <span className="foot-sep">·</span>
        <span className="foot-stat"><b>{live.chars.toLocaleString()}</b> characters</span>
        {selectedWords > 0 && (
          <>
            <span className="foot-sep">·</span>
            <span className="foot-stat foot-accent"><b>{selectedWords.toLocaleString()}</b> selected</span>
          </>
        )}
        {sceneTotal > 0 && (
          <>
            <span className="foot-sep">·</span>
            <span className="foot-stat">Scene {sceneIndex} of {sceneTotal}</span>
          </>
        )}
        <span className="foot-sep">·</span>
        <span className="foot-stat">Chapter: <b>{chapterWords.toLocaleString()}</b> words</span>
        <span className="foot-sep">·</span>
        <span className="foot-stat">Manuscript: <b>{projectWords.toLocaleString()}</b> words</span>
      </footer>
      <TtsConsole />
    </div>
  );
}

function ToolButton({ icon, title, onClick }: { icon: IconName; title: string; onClick: () => void }) {
  return (
    <button
      className="tool-btn"
      title={title}
      // Prevent focus loss from the editor so the command applies to the selection.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}

function SaveIndicator({ state, hasPending }: { state: SaveState; hasPending: boolean }) {
  const cls =
    state === "error" ? "save-ind save-error" : state === "saved" && !hasPending ? "save-ind save-ok" : "save-ind";
  return (
    <span className={cls}>
      {state === "saving" && <Icon name="clock" size={13} className="spin" />}
      {state === "saved" && !hasPending && <Icon name="check" size={13} />}
      {state === "unsaved" && <span className="dot" />}
      {state === "error" && <Icon name="alert" size={13} />}
      {SAVE_LABEL[state]}
    </span>
  );
}

const TTS_VOICE_KEY = "clovascribe:ttsVoice";

const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

function TtsControls({ tts, getText }: { tts: ReturnType<typeof useTts>; getText: () => string }) {
  const toast = useToast();
  const [voice, setVoice] = useState(() => {
    try {
      return localStorage.getItem(TTS_VOICE_KEY) ?? "";
    } catch {
      return "";
    }
  });

  const voices = tts.status?.voices ?? [];
  const multiVoice = voices.length > 1;
  const effectiveVoice =
    voice && voices.includes(voice) ? voice : tts.status?.defaultVoice || voices[0] || "narrator";
  const providerLabel = tts.providerLabel ?? (tts.status?.available ? tts.status.providerLabel : "Device Voice");
  // A configured provider that isn't reachable right now — surfaced, not hidden.
  const unreachable = (tts.status?.providers ?? []).filter((p) => !p.reachable);

  const start = async () => {
    const text = getText().trim();
    if (!text) {
      toast.info("Nothing to read yet — write something first.");
      return;
    }
    try {
      await tts.play(text, effectiveVoice);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Narration failed.");
    }
  };

  // Provider / device detail lives in the tooltip now, not as always-on chrome.
  const offlineNote = unreachable.length > 0 ? ` · ${unreachable.map((p) => p.label).join(", ")} offline` : "";
  const voiceTip = `Read aloud — ${providerLabel}, ${cap(effectiveVoice)}${offlineNote}`;

  if (tts.state === "idle") {
    return (
      <div className="voice-group" title={`Narration: ${providerLabel}${offlineNote}`}>
        {multiVoice && (
          <select
            className="tts-voice"
            value={effectiveVoice}
            title="Narration voice"
            onChange={(e) => {
              setVoice(e.target.value);
              try { localStorage.setItem(TTS_VOICE_KEY, e.target.value); } catch { /* ignore */ }
            }}
          >
            {voices.map((v) => (
              <option key={v} value={v}>{cap(v)}</option>
            ))}
          </select>
        )}
        <button className="btn btn-icon btn-ghost" title={voiceTip} onClick={start}>
          <Icon name="volume" size={16} />
          {unreachable.length > 0 && <span className="voice-warn-dot" />}
        </button>
      </div>
    );
  }

  return (
    <div className="voice-group tts-active" title={`Narration: ${providerLabel}${offlineNote}`}>
      <span className="tts-progress faint">{tts.progress.index}/{tts.progress.total}</span>
      {tts.state === "loading" ? (
        <Icon name="clock" size={15} className="spin" />
      ) : tts.state === "playing" ? (
        <button className="btn btn-icon btn-ghost" title="Pause" onClick={tts.pause}>
          <Icon name="pause" size={16} />
        </button>
      ) : (
        <button className="btn btn-icon btn-ghost" title="Resume" onClick={tts.resume}>
          <Icon name="play" size={16} />
        </button>
      )}
      <button className="btn btn-icon btn-ghost" title="Stop" onClick={tts.stop}>
        <Icon name="stop" size={15} />
      </button>
    </div>
  );
}
